<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, watch } from 'vue';
  import type { StayCriteria } from '@/entities/searchCriteria';
  import { Field, FieldSize, type SearchFormGoal } from '@/entities/searchForm';
  import DateFromDropdown from './DateFromDropdown.vue';
  import { STAY_DATES_TEXTS } from '../config/texts';
  import { useStayDates } from '../model/useStayDates';
  import { useStayDatesTracking } from '../model/useStayDatesTracking';

  interface StayDatesFieldProps {
    modelValue: StayCriteria;
    size?: `${FieldSize}`;
    loading?: boolean;
    disabled?: boolean;
    dropdownTarget?: HTMLElement | null;
  }

  const props = withDefaults(defineProps<StayDatesFieldProps>(), {
    size: FieldSize.M,
    loading: false,
    disabled: false,
    dropdownTarget: null,
  });

  interface StayDatesFieldEmits {
    (e: 'update:modelValue', value: StayCriteria): void;
    (e: 'manual'): void;
    (e: 'track', goal: SearchFormGoal): void;
    (e: 'open-change', isOpened: boolean): void;
  }

  const emit = defineEmits<StayDatesFieldEmits>();

  const model = computed<StayCriteria>({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  const stayDates = useStayDates({ model });
  const tracking = useStayDatesTracking({ stayDates, track: (goal) => emit('track', goal) });

  const onDayClick = (iso: string): void => {
    if (tracking.clickDate(iso)) {
      emit('manual');
    }
  };

  const onSelectFlexible = (daysFlexible: number): void => {
    tracking.setFlexible(daysFlexible);
    emit('manual');
  };

  watch(stayDates.controlActivated, (isOpened) => emit('open-change', isOpened));

  defineExpose({
    validate: (): boolean => tracking.validate(),
    close: (): void => tracking.close(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    class="stay-dates-field"
    data-testid="stay-dates-field"
  >
    <Field
      :label="STAY_DATES_TEXTS.fieldLabel"
      :active="stayDates.controlActivated.value"
      :error="stayDates.isInvalid.value"
      :loading="loading"
      :disabled="disabled"
      :size="size"
      value-testid="stay-dates-value"
      @click="tracking.toggle"
    >
      <template #value>
        <ClientOnly>
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ stayDates.valueLabel.value }}
            <span
              v-if="stayDates.hoverNightsLabel.value"
              :class="[
                'stay-dates-field__hover-nights',
                { 'stay-dates-field__hover-nights_error': stayDates.isInvalid.value },
              ]"
              data-testid="stay-dates-hover-nights"
            >
              · {{ stayDates.hoverNightsLabel.value }}
            </span>
          </Typo>
        </ClientOnly>
      </template>
    </Field>

    <DateFromDropdown
      range
      with-exact-option
      :range-value="stayDates.calendarValue.value"
      :flexible="stayDates.stay.value.flexible"
      :min-date="stayDates.minDate.value"
      :max-date="stayDates.maxDate.value"
      :disabled-from-date="stayDates.disabledFromDate.value"
      :visible="stayDates.controlActivated.value"
      :dropdown-target="dropdownTarget"
      @day-click="onDayClick"
      @day-hover="stayDates.hoverDate"
      @select-flexible="onSelectFlexible"
      @close="tracking.close"
    />
  </div>
</template>

<style scoped lang="scss">
  .stay-dates-field {
    position: relative;
    width: 100%;

    &__hover-nights {
      color: var(--color-text-secondary);

      &_error {
        color: inherit;
      }
    }
  }
</style>
